<template>
  <el-dialog
    v-model="visible"
    title="编辑子渠道"
    width="560px"
    align-center
    destroy-on-close
    class="edit-sub-channel-dialog"
    @closed="onDialogClosed"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      class="edit-sub-channel-form"
    >
      <div class="edit-sub-channel-form-fields">
        <el-form-item
          label="子渠道名称"
          prop="channelOrgName"
        >
          <el-input
            v-model="form.channelOrgName"
            clearable
            maxlength="20"
            show-word-limit
            placeholder="请输入子渠道名称"
            class="edit-form-control"
          />
        </el-form-item>
        <el-form-item
          label="子渠道类型"
          prop="channelIdentityType"
        >
          <el-select
            v-model="form.channelIdentityType"
            placeholder="请选择子渠道类型"
            clearable
            class="edit-form-control"
            @change="handleChannelIdentityTypeChange"
          >
            <el-option
              v-for="opt in identityOptionsForSelect"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isDistrictAssocSelected"
          label="所属区级文联"
          prop="channelDistrictType"
        >
          <el-select
            v-model="form.channelDistrictType"
            placeholder="请选择所属区级文联"
            clearable
            class="edit-form-control"
          >
            <el-option
              v-for="opt in channelDistrictTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="备注信息"
          prop="channelOrgRemarks"
        >
          <el-input
            v-model="form.channelOrgRemarks"
            type="textarea"
            :rows="5"
            placeholder="请输入备注信息"
            resize="none"
            maxlength="500"
            show-word-limit
            class="edit-form-control edit-form-textarea"
          />
        </el-form-item>
      </div>
    </el-form>
    <template #footer>
      <div class="edit-sub-channel-footer">
        <el-button @click="visible = false">
          取消
        </el-button>
        <el-button
          type="primary"
          :loading="submitLoading"
          @click="handleSubmit"
        >
          保存
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { updateGovernanceSubChannelUnderCurrentParent } from '@/api/enterprise'
import { GOVERNANCE_CHANNEL_IDENTITY_PRIMARY, GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC } from '@/enums/ChannelTypeEnum'

const emptyForm = () => ({
  id: null,
  channelOrgName: '',
  channelIdentityType: '',
  channelDistrictType: '',
  channelOrgRemarks: ''
})

export default {
  name: 'EditSubChannelDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    /** 当前编辑行：须含 id、channelOrgName、channelIdentityType、channelOrgRemarks */
    record: {
      type: Object,
      default: null
    },
    identityOptions: {
      type: Array,
      default: () => []
    },
    channelDistrictTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      form: emptyForm(),
      submitLoading: false,
      rules: {
        channelOrgName: [
          {
            required: true,
            validator: (_, val, callback) => {
              if (!(val || '').trim()) {
                callback(new Error('请输入子渠道名称'))
                return
              }
              callback()
            },
            trigger: ['blur', 'change']
          }
        ],
        channelIdentityType: [
          { required: true, message: '请选择子渠道类型', trigger: 'change' }
        ],
        channelDistrictType: [
          {
            validator: (_, val, callback) => {
              if (!this.isDistrictAssocSelected) {
                callback()
                return
              }
              if (!(val || '').trim()) {
                callback(new Error('请选择所属区级文联'))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  computed: {
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    identityOptionsForSelect() {
      const primary = GOVERNANCE_CHANNEL_IDENTITY_PRIMARY
      return (this.identityOptions || []).filter(
        opt => String(opt.value ?? '').trim() !== primary
      )
    },
    isDistrictAssocSelected() {
      return String(this.form.channelIdentityType ?? '').trim() === GOVERNANCE_CHANNEL_IDENTITY_DISTRICT_ASSOC
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.applyRecord(this.record)
        this.$nextTick(() => {
          this.$refs.formRef?.clearValidate()
        })
      }
    },
    record: {
      deep: true,
      handler(rec) {
        if (this.visible && rec) {
          this.applyRecord(rec)
        }
      }
    }
  },
  methods: {
    applyRecord(rec) {
      if (!rec || rec.id == null) {
        this.form = emptyForm()
        return
      }
      this.form = {
        id: rec.id,
        channelOrgName: rec.channelOrgName != null ? String(rec.channelOrgName) : '',
        channelIdentityType:
          rec.channelIdentityType != null ? String(rec.channelIdentityType).trim() : '',
        channelDistrictType:
          rec.channelDistrictType != null ? String(rec.channelDistrictType).trim() : '',
        channelOrgRemarks:
          rec.channelOrgRemarks != null ? String(rec.channelOrgRemarks) : ''
      }
    },
    handleChannelIdentityTypeChange() {
      if (!this.isDistrictAssocSelected) {
        this.form.channelDistrictType = ''
      }
      this.$nextTick(() => {
        this.$refs.formRef?.clearValidate('channelDistrictType')
      })
    },
    onDialogClosed() {
      this.form = emptyForm()
      this.$refs.formRef?.clearValidate()
    },
    handleSubmit() {
      this.$refs.formRef?.validate(async valid => {
        if (!valid) return
        const id = this.form.id
        if (id == null) {
          ElMessage.warning('缺少子渠道标识')
          return
        }
        this.submitLoading = true
        try {
          const res = await updateGovernanceSubChannelUnderCurrentParent({
            id,
            channelOrgName: (this.form.channelOrgName || '').trim(),
            channelIdentityType: this.form.channelIdentityType,
            channelDistrictType: this.isDistrictAssocSelected
              ? (this.form.channelDistrictType || '').trim()
              : undefined,
            channelOrgRemarks: (this.form.channelOrgRemarks || '').trim()
          })
          if (res && res.code === 200) {
            ElMessage.success(res.msg || '保存成功')
            this.visible = false
            this.$emit('success')
          }
        } catch (e) {
          console.error('保存子渠道失败:', e)
        } finally {
          this.submitLoading = false
        }
      })
    }
  }
}
</script>

<style lang="scss">
.edit-sub-channel-dialog.el-dialog {
  border-radius: 8px;
  padding: 0;

  .el-dialog__header {
    margin: 0;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__body {
    padding: 24px 24px 8px;
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
    border-top: none;
  }
}

.edit-sub-channel-form {
  .edit-sub-channel-form-fields {
    padding-left: 18px;
    padding-right: 18px;
    box-sizing: border-box;
  }

  .el-form-item {
    margin-bottom: 22px;
  }

  &.el-form--label-top .el-form-item__label {
    justify-content: flex-start;
    margin-bottom: 8px;
    padding: 0;
    height: auto;
    line-height: 1.4;
    font-weight: 600;
    font-size: 14px;
    color: #303133;
  }

  .edit-form-control {
    width: 100%;
  }

  .edit-form-textarea .el-textarea__inner {
    min-height: 120px !important;
    border-radius: 8px;
    padding: 10px 12px;
    line-height: 1.5;
  }

  .el-select .el-input__wrapper {
    min-height: 40px;
    border-radius: 8px;
  }
}

.edit-sub-channel-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

@media (max-width: 768px) {
  .edit-sub-channel-dialog.el-dialog {
    width: 100vw !important;
    max-width: 100vw;
    margin: 0 !important;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;

    .el-dialog__header {
      padding: 16px 44px 14px 16px;
      flex-shrink: 0;
    }

    .el-dialog__title {
      font-size: 16px;
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 12px;
    }

    .el-dialog__body {
      flex: 1;
      min-height: 0;
      padding: 16px 16px 8px;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .el-dialog__footer {
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
      flex-shrink: 0;
    }
  }

  .edit-sub-channel-form {
    .edit-sub-channel-form-fields {
      padding-left: 0;
      padding-right: 0;
    }

    .el-form-item {
      margin-bottom: 18px;
    }
  }

  .edit-sub-channel-footer {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;

    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

@media (max-width: 480px) {
  .edit-sub-channel-dialog.el-dialog {
    .el-dialog__header {
      padding: 14px 40px 12px 12px;
    }

    .el-dialog__body {
      padding: 12px 12px 8px;
    }

    .el-dialog__footer {
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    }
  }
}
</style>
